import { Fragment } from 'react';
import { InView } from 'react-intersection-observer';

import {
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { RiDeleteBinLine, RiStarFill } from '@remixicon/react';

import { ExternalRouteRef } from '@backstage/core-plugin-api';

import {
  DateUtils,
  DialogComposite,
  ListEmptyState,
  ResourceTableRow,
  StickyTableContainer,
  StickyTableHead,
} from '@spotify/confidence-core-react';
import {
  CollectionEntryFragment,
  useDeleteCollectionEntryMutation,
} from '@spotify/confidence-graphql';

import { useCollectionEntries, useDeleteCollectionEntry } from '../../hooks';
import {
  collectionsFlagRouteRef,
  collectionsMetricRouteRef,
} from '../../routes';

const useStyles = makeStyles(theme => ({
  table: {
    tableLayout: 'fixed',
    '& > tbody > tr > td': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  starIcon: {
    color: theme.palette.warning.main,
  },
}));

const getResourceFromEntry = (entry: CollectionEntryFragment) => {
  const resource = entry.resource;
  if (
    resource &&
    '__typename' in resource &&
    (resource.__typename === 'MetricsV1Metric' ||
      resource.__typename === 'FlagsAdminV1Flag')
  ) {
    return resource;
  }
  return undefined;
};

const getResourceType = (
  resource: ReturnType<typeof getResourceFromEntry>,
): string => {
  if (!resource) return 'Unknown';
  if (resource.__typename === 'MetricsV1Metric') return 'Metric';
  if (resource.__typename === 'FlagsAdminV1Flag') return 'Flag';
  return 'Unknown';
};

type CollectionEntriesListProps = {
  collectionName: string;
  isFavorites?: boolean;
};

export const CollectionEntriesList = ({
  collectionName,
  isFavorites,
}: CollectionEntriesListProps) => {
  const classes = useStyles();

  const {
    collectionEntries,
    isLoading,
    isEmpty,
    error,
    nextPage,
    hasNextPage,
  } = useCollectionEntries({ parent: collectionName });

  const { deleteCollectionEntry, isDeleting } = useDeleteCollectionEntry();
  const [deleteFavoriteEntry] = useDeleteCollectionEntryMutation({
    update: (cache, _, { variables }) => {
      if (variables?.name) {
        cache.evict({
          id: cache.identify({
            __typename: 'CollectionsV1CollectionEntry',
            name: variables.name,
          }),
        });
        cache.gc();
      }
    },
  });

  if (isEmpty) {
    return (
      <ListEmptyState
        title="This collection is empty"
        description="Resources added to this collection will appear here."
      />
    );
  }

  return (
    <Fragment>
      <StickyTableContainer
        loading={isLoading && !collectionEntries?.length}
        error={error}
      >
        <Table className={classes.table}>
          <StickyTableHead>
            <TableRow>
              {isFavorites && <TableCell width={48} padding="checkbox" />}
              <TableCell>Name</TableCell>
              <TableCell width="15%">Type</TableCell>
              <TableCell width="20%">Added</TableCell>
              {!isFavorites && <TableCell width={48} padding="checkbox" />}
            </TableRow>
          </StickyTableHead>
          <TableBody>
            {collectionEntries?.map(entry => {
              const resource = getResourceFromEntry(entry);
              const cells = (
                <>
                  {isFavorites && (
                    <TableCell
                      padding="checkbox"
                      onClick={e => e.stopPropagation()}
                    >
                      <IconButton
                        size="small"
                        className={classes.starIcon}
                        onClick={() =>
                          deleteFavoriteEntry({
                            variables: { name: entry.name },
                          })
                        }
                      >
                        <RiStarFill size={18} />
                      </IconButton>
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body1">
                      {resource?.displayName ?? entry.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" color="textSecondary">
                      {getResourceType(resource)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body1" color="textSecondary">
                      {entry.createTime
                        ? DateUtils.xAgo(new Date(entry.createTime))
                        : '-'}
                    </Typography>
                  </TableCell>
                  {!isFavorites && (
                    <TableCell
                      padding="checkbox"
                      onClick={e => e.stopPropagation()}
                    >
                      <DialogComposite.Root>
                        <DialogComposite.Trigger>
                          <IconButton size="small" disabled={isDeleting}>
                            <RiDeleteBinLine size={18} />
                          </IconButton>
                        </DialogComposite.Trigger>
                        <DialogComposite.Content>
                          <DialogComposite.Header>
                            <DialogComposite.Title>
                              Remove from collection
                            </DialogComposite.Title>
                            <DialogComposite.CloseButton />
                          </DialogComposite.Header>
                          <DialogComposite.Body>
                            <Typography>
                              Are you sure you want to remove{' '}
                              <strong>
                                {resource?.displayName ?? 'this item'}
                              </strong>{' '}
                              from this collection?
                            </Typography>
                          </DialogComposite.Body>
                          <DialogComposite.Actions>
                            <DialogComposite.Actions.Cancel>
                              Cancel
                            </DialogComposite.Actions.Cancel>
                            <DialogComposite.Actions.Primary
                              onClick={() => deleteCollectionEntry(entry.name)}
                              disabled={isDeleting}
                            >
                              Remove
                            </DialogComposite.Actions.Primary>
                          </DialogComposite.Actions>
                        </DialogComposite.Content>
                      </DialogComposite.Root>
                    </TableCell>
                  )}
                </>
              );
              const routeRef =
                resource?.__typename === 'FlagsAdminV1Flag'
                  ? collectionsFlagRouteRef
                  : collectionsMetricRouteRef;

              return resource ? (
                <ResourceTableRow
                  key={entry.name}
                  routeRef={routeRef as ExternalRouteRef}
                  name={resource.name}
                >
                  {cells}
                </ResourceTableRow>
              ) : (
                <TableRow key={entry.name}>{cells}</TableRow>
              );
            })}
          </TableBody>
        </Table>
      </StickyTableContainer>
      {hasNextPage && (
        <InView
          onChange={async inView => {
            if (inView) await nextPage();
          }}
        />
      )}
    </Fragment>
  );
};
